import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button, IconButton, SegmentedControl} from '@primer/react'
import {useState} from 'react'
import ReactMarkdown from 'react-markdown'

import styles from './TraceDetails.module.css'
import type {DecoratedNode, StringyNode} from './utilities/tree-utilities'

const MARKDOWN_DETECTION_REGEX =
  /^#{1,6}\s|^\*{1,2}[\s\S]*?\*{1,2}|^\[.*?\]\(.*?\)|```[\s\S]*?```|^\s*[-*+]\s|^\d+\.\s/m
const MAX_NESTED_DEPTH = 4

function renderJsonContent(value: unknown): React.ReactElement {
  if (typeof value === 'string') {
    // Check if the string 'looks like' markdown using regex
    if (MARKDOWN_DETECTION_REGEX.test(value)) {
      return <ReactMarkdown>{value}</ReactMarkdown>
    }
    return <pre className={styles.wrappedPre}>{value}</pre>
  }

  return <pre className={styles.wrappedPre}>{JSON.stringify(value, null, 2)}</pre>
}

const TraceView = {
  FORMATTED: 0,
  JSON: 1,
} as const
type TraceView = (typeof TraceView)[keyof typeof TraceView]

export function TraceDetails({node}: {node: StringyNode | DecoratedNode}) {
  const [selectedView, setSelectedView] = useState<TraceView>(TraceView.FORMATTED)
  const handleChange = (selectedIndex: number) => {
    if (selectedIndex === TraceView.FORMATTED || selectedIndex === TraceView.JSON) {
      setSelectedView(selectedIndex)
    }
  }

  return (
    <div className={styles.traceDetails}>
      <div className={styles.header}>
        <div className={styles.traceName}>{node.name}</div>
        <div className={styles.duration}>duration: {node.duration}ms</div>
      </div>

      <SegmentedControl aria-label="Trace formatted/JSON view" onChange={handleChange} size="small">
        <SegmentedControl.Button value={TraceView.FORMATTED} selected={selectedView === TraceView.FORMATTED}>
          Formatted
        </SegmentedControl.Button>
        <SegmentedControl.Button value={TraceView.JSON} selected={selectedView === TraceView.JSON}>
          JSON
        </SegmentedControl.Button>
      </SegmentedControl>

      <div className={styles.section}>
        <table className={styles.table}>
          <tbody>
            <tr className={styles.tableRow} key={'Input'}>
              <td className={`${styles.tableCell} ${styles.sectionTitle}`}>Input</td>
              <td className={styles.tableCell}>
                {selectedView === TraceView.FORMATTED ? <Value value={node.input} /> : renderJsonContent(node.input)}
              </td>
            </tr>
            <tr className={styles.tableRow} key={'Output'}>
              <td className={`${styles.tableCell} ${styles.sectionTitle}`}>Output</td>
              <td className={styles.tableCell}>
                {selectedView === TraceView.FORMATTED ? <Value value={node.output} /> : renderJsonContent(node.output)}
              </td>
            </tr>
            <tr className={styles.tableRow} key={'Metadata'}>
              <td className={`${styles.tableCell} ${styles.sectionTitle}`}>Metadata</td>
              <td className={styles.tableCell}>
                {selectedView === TraceView.FORMATTED ? (
                  <Value value={node.metadata} />
                ) : (
                  renderJsonContent(node.metadata)
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Value({value, depth = 1}: {value: unknown; depth?: number}) {
  if (depth > MAX_NESTED_DEPTH) {
    return <StringValue value={JSON.stringify(value, null, 2)} />
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <StringValue value={String(value)} />
  } else if (Array.isArray(value)) {
    return <ArrayValue value={value} depth={depth} />
  } else if (typeof value === 'object' && value !== null) {
    return <ObjectValue value={value} depth={depth} />
  } else {
    return 'Unknown'
  }
}

function StringValue({value}: {value: string}) {
  const isLong = value.length > 100
  const trimmedValue = value.length > 100 ? `${value.slice(0, 100)}...` : value
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      {isLong && <ExpandButton expanded={expanded} onClick={() => setExpanded(prev => !prev)} />}
      {expanded ? renderJsonContent(value) : trimmedValue}
    </>
  )
}

function ArrayValue({value, depth}: {value: unknown[]; depth: number}) {
  return (
    <ul>
      {value.map((item, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <li key={index}>
          <Value value={item} depth={depth + 1} />
        </li>
      ))}
    </ul>
  )
}

function ObjectValue({value, depth}: {value: object; depth: number}) {
  const [expanded, setExpanded] = useState(() => Object.keys(value).length <= 2)
  return (
    <div className={styles.expandable}>
      <ExpandButton
        expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
        text={`{${Object.keys(value).length}}`}
      />
      {expanded && (
        <div className={styles.expandableContent}>
          <table className={styles.table}>
            <tbody>
              {Object.entries(value).map(([key, val]) => (
                <tr className={styles.tableRow} key={key}>
                  <td className={styles.tableCell}>{key}</td>
                  <td className={styles.tableCell}>
                    <Value value={val} depth={depth + 1} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ExpandButton({expanded, onClick, text}: {expanded: boolean; onClick: () => void; text?: string}) {
  return text ? (
    <Button
      leadingVisual={expanded ? ChevronDownIcon : ChevronRightIcon}
      onClick={onClick}
      size="small"
      variant="invisible"
    >
      {text}
    </Button>
  ) : (
    <IconButton
      icon={expanded ? ChevronDownIcon : ChevronRightIcon}
      aria-label={expanded ? 'Collapse' : 'Expand'}
      onClick={onClick}
      size="small"
      variant="invisible"
    />
  )
}
